// 네트워크 "전면 실패" 감지 (2026-07-24)
//
// 배경: 유니콘앱류(DNS 변경 + Fake VPN 프로파일)를 켜두면 iOS의 "VPN 프로파일 1개만 활성"
//       제약 아래 전체 트래픽이 후킹돼 정상 도메인의 DNS 해석·HTTPS 연결까지 간섭받는다.
//       실측(2026-07-24): prod는 화면마다 전면 실패, 로컬 dev는 일부 성공 후 전면 실패(간헐 불안정).
//       서버는 무죄인데 유저는 "앱이 고장났다"고 읽는다 → 스스로 원인을 알 수 있게 힌트가 필요.
//
// 설계 원칙 — 오탐(노이즈)을 최우선으로 막는다:
//   1) **전송계층 실패만** 센다. 서버가 status를 돌려준 4xx/5xx는 네트워크가 살아있다는 증거라 제외.
//   2) 단발 실패는 무시 — 연속 MIN_FAILURES회 이상.
//   3) 서로 다른 엔드포인트 MIN_DISTINCT_PATHS개 이상 — API 하나만 죽은 서버 장애와 구분.
//   4) 성공이 1건이라도 들어오면 즉시 초기화 — 회복되면 힌트도 바로 사라진다.
//   5) 마지막 실패 후 STALE_MS가 지나면 누적을 버린다 — 어제 실패가 오늘 힌트를 켜지 않게.

import { useSyncExternalStore } from "react";

const MIN_FAILURES = 3; // 연속 전송실패 최소 횟수 (apiFetch 내부 3회 재시도는 '1회'로 계산됨)
const MIN_DISTINCT_PATHS = 2; // 서로 다른 엔드포인트 수
const STALE_MS = 30_000; // 이 시간 이상 조용하면 누적 초기화

let failures = 0;
let failedPaths = new Set<string>();
let lastFailureAt = 0;
let troubled = false;

const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function setTroubled(next: boolean) {
  if (troubled === next) return;
  troubled = next;
  emit();
}

function clearCounters() {
  failures = 0;
  failedPaths = new Set();
  lastFailureAt = 0;
}

/**
 * 경로 정규화 — 같은 API를 종목코드/ID만 바꿔 여러 번 호출한 걸
 * "서로 다른 엔드포인트"로 오인하지 않도록 앞 3세그먼트까지만 남긴다.
 *   /api/mobile/quote/005930?x=1 → /api/mobile/quote
 *   /api/mobile/review/list      → /api/mobile/review
 */
function normalizePath(path: string): string {
  const withoutQuery = path.split("?")[0];
  const segments = withoutQuery.replace(/^https?:\/\/[^/]+/, "").split("/").filter(Boolean);
  return "/" + segments.slice(0, 3).join("/");
}

/** 전송계층 실패(fetch reject·타임아웃) 1건 기록. HTTP 응답을 받은 경우엔 호출하지 않는다. */
export function recordTransportFailure(path: string): void {
  const now = Date.now();
  if (lastFailureAt && now - lastFailureAt > STALE_MS) clearCounters();

  failures += 1;
  failedPaths.add(normalizePath(path));
  lastFailureAt = now;

  setTroubled(failures >= MIN_FAILURES && failedPaths.size >= MIN_DISTINCT_PATHS);
}

/** 응답 수신 성공(status 무관) — 네트워크 경로가 살아있다는 뜻이라 누적을 전부 버린다. */
export function recordNetworkSuccess(): void {
  if (failures === 0 && !troubled) return;
  clearCounters();
  setTroubled(false);
}

export function isNetworkTroubled(): boolean {
  return troubled;
}

export function subscribeNetHealth(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** 테스트 전용 — 모듈 전역 상태 초기화 */
export function resetNetHealthForTest(): void {
  clearCounters();
  troubled = false;
  listeners.clear();
}

/** 화면에서 "지금 전면 실패 상태인가?"를 구독 */
export function useNetworkTrouble(): boolean {
  return useSyncExternalStore(subscribeNetHealth, isNetworkTroubled, () => false);
}
